<template>
  <div class="d-flex flex-column justify-content-end w-100">
    <label
      v-if="itemModel.vars.label"
      :for="itemModel.vars.id"
      :class="
        'form-label' +
        (itemModel.vars.label_attr && itemModel.vars.label_attr.class ? ' ' + itemModel.vars.label_attr.class : '') +
        (itemModel.vars.required ? ' required' : '')
      "
    >
      {{ itemModel.vars.label }}{{ itemModel.vars.required ? "*" : "" }}
      <span
        v-if="itemModel.vars.help && !isMobile()"
        v-tooltip="{ title: itemModel.vars.help, textWrap: true }"
        class="form-info ms-1"
      >
        <FontAwesomeIcon icon="fa-light fa-circle-question" />
      </span>
    </label>
    <div v-if="isMobile() && itemModel.vars.help" class="form-info">
      <FontAwesomeIcon icon="fa-light fa-circle-question" />
      {{ itemModel.vars.help }}
    </div>
    <div class="color-picker">
      <button
        :id="itemModel.vars.id"
        ref="trigger"
        type="button"
        class="color-picker__trigger"
        :class="{ '-open': isOpen }"
        :disabled="itemModel.vars.disabled === true"
        :aria-label="itemModel.vars.label ? null : $t('color.open')"
        aria-haspopup="dialog"
        :aria-expanded="isOpen ? 'true' : 'false'"
        :aria-controls="popoverId"
        @focus="focus"
        @click="toggle"
      >
        <span v-if="isEmpty" v-tooltip="{ title: $t('color.noColor') }" class="color-picker__empty"></span>
        <span v-else class="color-picker__swatch" :style="{ backgroundColor: color }"></span>
      </button>
      <div
        v-if="isOpen"
        :id="popoverId"
        ref="popover"
        class="color-picker__popover"
        role="dialog"
        :aria-label="$t('color.picker')"
        @keydown.esc.stop.prevent="close(true)"
        @focusout="onFocusOut"
      >
        <div
          ref="area"
          class="color-picker__area"
          :style="{ backgroundColor: hueColor }"
          role="slider"
          tabindex="0"
          :aria-label="$t('color.saturation')"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-valuenow="Math.round(saturation)"
          :aria-valuetext="
            $t('color.saturationValue', { saturation: Math.round(saturation), value: Math.round(value) })
          "
          @pointerdown="onAreaPointerDown"
          @pointermove="onAreaPointerMove"
          @pointerup="onAreaPointerUp"
          @pointercancel="onAreaPointerUp"
          @keydown="onAreaKeydown"
        >
          <span class="color-picker__cursor" :style="cursorStyle"></span>
        </div>
        <input
          v-model.number="hue"
          type="range"
          class="color-picker__hue"
          min="0"
          max="360"
          step="1"
          :aria-label="$t('color.hue')"
          @input="updatePreview"
          @change="commit"
        />
        <div class="color-picker__rgb">
          <label v-for="channel in rgbChannels" :key="channel.key" class="color-picker__channel">
            <span class="color-picker__channel-letter" aria-hidden="true">{{ channel.letter }}</span>
            <input
              type="number"
              class="form-control color-picker__channel-input"
              min="0"
              max="255"
              step="1"
              inputmode="numeric"
              :value="rgb[channel.key]"
              :aria-label="$t(channel.label)"
              @input="inputRgb(channel.key, $event.target.value)"
              @blur="blurRgb(channel.key, $event)"
            />
          </label>
        </div>
      </div>
      <input
        v-model="hex"
        type="text"
        :class="['form-control', 'color-picker__hex', computedClass]"
        :disabled="itemModel.vars.disabled === true"
        :readonly="itemModel.vars.attr && itemModel.vars.attr.readonly"
        :aria-label="$t('color.hexValue')"
        :aria-describedby="itemModel.vars.error ? errorId : null"
        :aria-invalid="isInvalid ? 'true' : null"
        maxlength="7"
        placeholder="#000000"
        @blur="blurHex"
        @input="inputHex"
      />
    </div>
    <div v-if="suggestedColors.length" class="color-picker__suggestions" role="group" :aria-label="$t('color.presets')">
      <button
        v-for="suggestedColor in suggestedColors"
        :key="suggestedColor"
        v-tooltip="{ title: suggestedColor }"
        type="button"
        class="color-picker__suggestion"
        :class="{ '-selected': isSelected(suggestedColor) }"
        :style="{ backgroundColor: suggestedColor }"
        :disabled="itemModel.vars.disabled === true"
        :aria-pressed="isSelected(suggestedColor)"
        :aria-label="suggestedColor"
        @click="selectSuggestedColor(suggestedColor)"
      ></button>
    </div>
    <div v-if="itemModel.vars.error" :id="errorId" class="invalid-feedback d-block">
      {{ itemModel.vars.error }}
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { createPopper } from "@popperjs/core";
import { colorManager } from "@/services/platform/color-manager";
import { formValidator } from "@/services/form/form-validator";

const DEFAULT_COLOR = "#000000";
const KEYBOARD_STEP = 1;
const KEYBOARD_LARGE_STEP = 10;
const RGB_CHANNELS = Object.freeze([
  { key: "red", letter: "R", label: "color.red" },
  { key: "green", letter: "G", label: "color.green" },
  { key: "blue", letter: "B", label: "color.blue" },
]);
const RGB_PATTERN = /^\d{1,3}$/;

export default {
  name: "ColorPickerComponent",
  components: {
    FontAwesomeIcon,
  },
  props: {
    model: {
      type: Object,
      required: true,
    },
  },
  emits: ["update", "focus", "draft"],
  data() {
    const color = this.normalize(this.model?.vars?.value);
    return {
      itemModel: this.model,
      color,
      hex: this.initialHex(this.model?.vars?.value),
      ...colorManager.hexToHsv(color),
      isOpen: false,
      isDragging: false,
      popper: null,
    };
  },
  computed: {
    suggestedColors() {
      const colors = this.itemModel.vars.attr && this.itemModel.vars.attr.suggestedColors;
      if (!colors) {
        return [];
      }
      return Object.values(colors)
        .map((color) => this.toHex(color))
        .filter((color, index, list) => color && list.indexOf(color) === index);
    },
    isEmpty() {
      return !this.hex.trim();
    },
    errorId() {
      return this.itemModel.vars.id + "_error";
    },
    popoverId() {
      return this.itemModel.vars.id + "_popover";
    },
    rgbChannels() {
      return RGB_CHANNELS;
    },
    rgb() {
      return colorManager.hexToRgb(this.color);
    },
    hueColor() {
      return colorManager.hsvToHex(this.hue, 100, 100);
    },
    cursorStyle() {
      return {
        left: this.saturation + "%",
        top: 100 - this.value + "%",
        backgroundColor: this.color,
      };
    },
    isInvalid() {
      return Boolean(this.itemModel.vars.error || this.itemModel.vars.isInvalid);
    },
    computedClass() {
      if (this.isInvalid) {
        return "is-invalid";
      } else if (!this.itemModel.vars.error && this.itemModel.vars.required) {
        return "is-valid";
      }
      return "";
    },
  },
  watch: {
    model() {
      this.itemModel = this.model;
      this.color = this.normalize(this.model?.vars?.value);
      this.hex = this.initialHex(this.model?.vars?.value);
    },
    color(newColor) {
      // Resynchro TSV uniquement si la couleur vient d'ailleurs (saisie hex, suggestion) : sur
      // un gris la teinte n'est pas déductible de l'hex et serait sinon ramenée à 0 en plein drag
      if (colorManager.hsvToHex(this.hue, this.saturation, this.value) !== newColor) {
        this.syncHsv();
      }
    },
  },
  mounted() {
    // Champ requis sans valeur initiale : l'aperçu affiche déjà #000000 (fallback de
    // normalize()/initialHex()), vars.value est aligné dessus sans émettre, pour ne pas
    // marquer le formulaire comme modifié sans action de l'utilisateur
    if (this.itemModel.vars.required === true && !this.model?.vars?.value) {
      this.itemModel.vars.value = DEFAULT_COLOR;
      formValidator.validModel(this.itemModel);
    }
  },
  beforeUnmount() {
    this.destroyPopover();
  },
  methods: {
    toHex(value) {
      return colorManager.toHex(value);
    },
    normalize(value) {
      return this.toHex(value) || DEFAULT_COLOR;
    },
    initialHex(value) {
      const hex = this.toHex(value);
      if (hex) {
        return hex;
      }
      // Champ optionnel sans valeur : on laisse le champ vide pour afficher l'aperçu « aucune couleur »
      return this.model?.vars?.required === true ? DEFAULT_COLOR : "";
    },
    isSelected(suggestedColor) {
      return suggestedColor === this.color.toLowerCase();
    },
    apply(value) {
      this.color = value;
      this.hex = value;
      this.itemModel.vars.value = value;
      // Contrairement aux autres *TypeComponent (InputType, SelectType, DateType...), rien ici
      // ne revalidait vars.error/isInvalid après un changement : le champ restait affiché comme
      // invalide (ex. après avoir choisi #000000 explicitement) jusqu'à ce qu'une validation
      // déclenchée ailleurs dans le formulaire le recalcule.
      formValidator.validModel(this.itemModel);
      this.$emit("update", value);
      this.$emit("draft", this.itemModel);
    },
    focus() {
      this.$emit("focus");
    },
    syncHsv() {
      Object.assign(this, colorManager.hexToHsv(this.color));
    },
    updatePreview() {
      // Pendant le glissement on se contente de l'aperçu, le modèle n'est mis à jour qu'au relâchement
      this.color = colorManager.hsvToHex(this.hue, this.saturation, this.value);
      this.hex = this.color;
    },
    commit() {
      this.apply(this.color);
    },
    applyChannel(channel, level) {
      const rgb = { ...this.rgb, [channel]: level };
      this.apply(colorManager.rgbToHex(rgb.red, rgb.green, rgb.blue));
    },
    inputRgb(channel, raw) {
      // Saisie partielle ou hors bornes laissée telle quelle pendant la frappe, bornée au blur
      if (RGB_PATTERN.test(raw) && Number(raw) <= 255) {
        this.applyChannel(channel, Number(raw));
      }
    },
    blurRgb(channel, event) {
      const raw = event.target.value.trim();
      if (RGB_PATTERN.test(raw)) {
        this.applyChannel(channel, Math.min(Number(raw), 255));
      }
      // :value ne repatche pas le DOM si la couleur n'a pas changé, d'où la resynchro manuelle
      event.target.value = this.rgb[channel];
    },
    toggle() {
      if (this.isOpen) {
        this.close(false);
      } else {
        this.open();
      }
    },
    async open() {
      this.isOpen = true;
      document.addEventListener("pointerdown", this.onDocumentPointerDown);
      await this.$nextTick();
      if (!this.isOpen || !this.$refs.popover) {
        return;
      }
      this.popper = createPopper(this.$refs.trigger, this.$refs.popover, {
        placement: "bottom-start",
        strategy: "fixed",
        modifiers: [{ name: "offset", options: { offset: [0, 8] } }],
      });
      this.$refs.area.focus();
    },
    close(restoreFocus) {
      if (!this.isOpen) {
        return;
      }
      if (this.isDragging) {
        this.isDragging = false;
        this.commit();
      }
      this.isOpen = false;
      this.destroyPopover();
      if (restoreFocus) {
        this.$refs.trigger.focus();
      }
    },
    destroyPopover() {
      document.removeEventListener("pointerdown", this.onDocumentPointerDown);
      if (this.popper) {
        this.popper.destroy();
        this.popper = null;
      }
    },
    isInsidePicker(target) {
      return Boolean(target && (this.$refs.popover?.contains(target) || this.$refs.trigger?.contains(target)));
    },
    onDocumentPointerDown(event) {
      if (!this.isInsidePicker(event.target)) {
        this.close(false);
      }
    },
    onFocusOut(event) {
      if (event.relatedTarget && !this.isInsidePicker(event.relatedTarget)) {
        this.close(false);
      }
    },
    updateAreaFromPointer(event) {
      const rect = this.$refs.area.getBoundingClientRect();
      const ratioX = rect.width ? (event.clientX - rect.left) / rect.width : 0;
      const ratioY = rect.height ? (event.clientY - rect.top) / rect.height : 0;
      this.saturation = Math.min(Math.max(ratioX, 0), 1) * 100;
      this.value = (1 - Math.min(Math.max(ratioY, 0), 1)) * 100;
      this.updatePreview();
    },
    onAreaPointerDown(event) {
      event.preventDefault();
      this.$refs.area.setPointerCapture?.(event.pointerId);
      this.$refs.area.focus();
      this.isDragging = true;
      this.updateAreaFromPointer(event);
    },
    onAreaPointerMove(event) {
      if (this.isDragging) {
        this.updateAreaFromPointer(event);
      }
    },
    onAreaPointerUp() {
      if (!this.isDragging) {
        return;
      }
      this.isDragging = false;
      this.commit();
    },
    onAreaKeydown(event) {
      const step = event.shiftKey ? KEYBOARD_LARGE_STEP : KEYBOARD_STEP;
      const moves = {
        ArrowLeft: ["saturation", -step],
        ArrowRight: ["saturation", step],
        ArrowDown: ["value", -step],
        ArrowUp: ["value", step],
      };
      const move = moves[event.key];
      if (!move) {
        return;
      }
      event.preventDefault();
      const [channel, delta] = move;
      this[channel] = Math.min(Math.max(this[channel] + delta, 0), 100);
      this.updatePreview();
      this.commit();
    },
    inputHex() {
      // La notation courte n'est étendue qu'au blur pour ne pas réécrire la saisie en cours
      const hex = this.hex.length === 7 ? this.toHex(this.hex) : null;
      if (hex) {
        this.apply(hex);
      }
    },
    blurHex() {
      if (!this.hex.trim() && this.itemModel.vars.required !== true) {
        this.clear();
        return;
      }
      if (!this.hex.trim()) {
        this.apply(DEFAULT_COLOR);
        return;
      }
      const hex = this.toHex(this.hex);
      if (!hex) {
        this.hex = this.color;
        return;
      }
      this.apply(hex);
    },
    clear() {
      this.color = DEFAULT_COLOR;
      this.hex = "";
      this.itemModel.vars.value = null;
      formValidator.validModel(this.itemModel);
      this.$emit("update", null);
      this.$emit("draft", this.itemModel);
    },
    selectSuggestedColor(suggestedColor) {
      this.apply(suggestedColor);
    },
    isMobile() {
      return window.innerWidth < 992;
    },
  },
};
</script>

<style lang="scss" scoped>
.color-picker {
  display: flex;
  align-items: center;
  gap: 8px;
}

.color-picker__trigger {
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
  width: 48px;
  height: 40px;
  padding: 4px;
  border: 1px solid $grey;
  border-radius: $radius;
  background: $white;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover:not(:disabled),
  &.-open {
    border-color: var(--primary-color);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  @include dark-theme {
    background: $dark-theme-bg;
    border-color: $m-color_12;

    &:hover:not(:disabled),
    &.-open {
      border-color: $blue;
    }
  }
}

.color-picker__swatch {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: calc($radius / 2);
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.1);
}

.color-picker__empty {
  position: absolute;
  inset: 4px;
  overflow: hidden;
  border: 1px solid $grey;
  border-radius: calc($radius / 2);
  background: transparent;

  // Bande rouge oblique d'angle à angle, épaisseur en rem pour rester fluide au zoom
  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: -0.625rem;
    width: calc(100% + 1.25rem);
    height: 0.25rem;
    border-radius: 0.125rem;
    background: $red;
    transform: translateY(-50%) rotate(40deg);
  }

  @include dark-theme {
    border-color: $m-color_12;
  }
}

.color-picker__popover {
  z-index: 1070;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 240px;
  padding: 12px;
  border: 1px solid $m-color_2;
  border-radius: $radius;
  background: $white;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);

  @include dark-theme {
    border-color: $m-color_12;
    background: $m-color_10;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  }
}

.color-picker__area {
  position: relative;
  height: 150px;
  border-radius: calc($radius / 2);
  background-image: linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent);
  cursor: crosshair;
  touch-action: none;

  &:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }
}

.color-picker__cursor {
  position: absolute;
  width: 16px;
  height: 16px;
  border: 2px solid $white;
  border-radius: 50%;
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.3),
    0 1px 3px rgba(0, 0, 0, 0.4);
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.color-picker__hue {
  width: 100%;
  height: 12px;
  margin: 0;
  border-radius: 6px;
  background: linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00);
  cursor: pointer;
  appearance: none;

  &::-webkit-slider-thumb {
    width: 16px;
    height: 16px;
    border: 2px solid $white;
    border-radius: 50%;
    background: transparent;
    box-shadow:
      0 0 0 1px rgba(0, 0, 0, 0.3),
      0 1px 3px rgba(0, 0, 0, 0.4);
    appearance: none;
  }

  &::-moz-range-thumb {
    width: 12px;
    height: 12px;
    border: 2px solid $white;
    border-radius: 50%;
    background: transparent;
    box-shadow:
      0 0 0 1px rgba(0, 0, 0, 0.3),
      0 1px 3px rgba(0, 0, 0, 0.4);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }
}

.color-picker__rgb {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}

.color-picker__channel {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
}

.color-picker__channel-letter {
  flex: 0 0 auto;
  font-size: 0.75rem;
  font-weight: 600;
  color: $m-color_11;

  @include dark-theme {
    color: $white;
  }
}

.color-picker__channel-input {
  min-width: 0;
  padding: 4px 6px;
  font-size: 0.875rem;
  text-align: center;
  appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    margin: 0;
    appearance: none;
  }
}

.color-picker__hex {
  text-transform: lowercase;
}

.color-picker__suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.color-picker__suggestion {
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1px solid $grey;
  border-radius: calc($radius / 2);
  cursor: pointer;

  &.-selected {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }

  &:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  @include dark-theme {
    border-color: $m-color_12;
  }
}
</style>
